import { useState, useEffect, useRef } from "react";

interface UseNumberAnimationProps {
  targetNumber: number;
  duration?: number;
  hasRun: boolean;
}

export const useNumberAnimation = ({
  targetNumber,
  duration = 2000,
  hasRun,
}: UseNumberAnimationProps) => {
  const [displayNumber, setDisplayNumber] = useState("0");
  const [animate, setAnimate] = useState(false);
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (!hasRun) return;

    const animateNumber = () => {
      let startTime: number | null = null;
      let currentNumber = 0;
      let animationFrameId: number;

      setAnimate(true);

      const updateCount = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const progress = timestamp - startTime;
        const progressRatio = Math.min(progress / duration, 1);

        const easeOutExpo = 1 - Math.pow(2, -10 * progressRatio);
        currentNumber = Math.floor(easeOutExpo * targetNumber);

        setDisplayNumber(currentNumber.toString());

        if (progress < duration) {
          animationFrameId = requestAnimationFrame(updateCount);
        } else {
          setDisplayNumber(targetNumber.toString());
        }
      };

      animationFrameId = requestAnimationFrame(updateCount);

      return () => {
        cancelAnimationFrame(animationFrameId);
      };
    };

    return animateNumber();
  }, [targetNumber, duration, hasRun]);

  return { displayNumber, animate, hasRunRef };
};
